import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Accordion from '../../../components/Accordion/Accordion';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ListItem from '../../../components/ListItem/ListItem';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Insurance & Payers Solutions | General Genomics"
        description="Learn how General Genomics uses synthetic and de-identified data to optimize operations, reduce risks, and enhance decision-making for insurers and payers with secure, scalable, and compliant solutions."
      />
      <Hero
        helper="Industries &gt; Commercial"
        headline="Innovative data solutions for insurance and payer success"
      />
      <Section theme="" className="cards-large-icons">
        {' '}
        <h2 className="action-on-view action-fade-in">
          Transform Your Insurance Operations
        </h2>{' '}
        <p className="action-on-view action-fade-in">
          {' '}
          General Genomics empowers insurers and payers with cutting-edge
          solutions leveraging synthetic and de-identified data to optimize
          operations, reduce risks, and enhance decision-making. Our tailored
          approach addresses your unique challenges in risk analysis, fraud
          prevention, and claims optimization, all while ensuring full
          compliance with industry regulations and data security standards. By
          utilizing secure and scalable data, we deliver actionable insights
          that drive efficiency and profitability in the insurance sector.{' '}
        </p>{' '}
        <Image
          path="/images/library/doctor-patient.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />{' '}
      </Section>

      <ContentSplit image="/images/library/man.webp" position="right">
        {' '}
        <p className="helper action-on-view action-fade-in">
          Key Features of Our Solution
        </p>{' '}
        <h2 className="action-on-view action-fade-in">
          How We Help Insurers and Payers
        </h2>{' '}
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          {' '}
          <p>
            <strong>Risk Analysis:</strong> Leverage synthetic data (SYN46™) to
            predict and mitigate risks, improving your decision-making
            capabilities and enhancing operational efficiency.
          </p>{' '}
        </ListItem>{' '}
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          {' '}
          <p>
            <strong>Fraud Prevention:</strong> Utilize de-identified data to
            identify fraudulent patterns and prevent financial losses, ensuring
            security and compliance.
          </p>{' '}
        </ListItem>{' '}
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          {' '}
          <p>
            <strong>Claims Optimization:</strong> Optimize claims processing
            with actionable insights, reducing operational overhead and
            enhancing customer satisfaction.
          </p>{' '}
        </ListItem>{' '}
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          {' '}
          <p>
            <strong>Data Security & Compliance:</strong> Our solutions meet
            industry standards for privacy and regulatory compliance, ensuring
            the secure handling of sensitive data.
          </p>{' '}
        </ListItem>{' '}
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About Our Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover additional insights into our synthetic data (SYN46™), custom
          solutions, and commitment to data security. Explore how we can help
          your business thrive.
        </p>
        <div className="card-group">
          <Card
            icon="data_usage"
            headline="Synthetic & De-Identified Data"
            subhead="Learn about our advanced data solutions that protect privacy while offering valuable insights."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="build"
            headline="Custom Data Solutions"
            subhead="Explore our tailored approaches for insurers and payers, optimizing data for unique business needs."
            btnURL="/industries/commercial/custom-informatics"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="lock"
            headline="Data Governance & Compliance"
            subhead="Understand our commitment to secure, compliant data handling and industry regulations."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/office-startup-3.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently Asked Questions About General Genomics’ Insurance and
          Payers Solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics enhances insurance operations with secure,
          synthetic data (SYN46™) solutions for risk analysis, fraud
          prevention, and compliance.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics help insurance companies optimize operations?"
          >
            General Genomics leverages synthetic and de-identified data to
            streamline operations, enhance risk analysis, and improve
            decision-making, helping insurers reduce costs and improve
            efficiency.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What role does data security play in General Genomics’ solutions for insurers?"
          >
            Data security is paramount in all General Genomics&apos; solutions.
            The company ensures strict compliance with industry regulations,
            safeguarding sensitive data while improving operational efficiency
            for insurers.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics address challenges like fraud prevention and claims optimization?"
          >
            General Genomics uses synthetic data (SYN46™) and advanced
            analytics to improve fraud detection and claims optimization,
            enabling insurers to identify risks earlier and reduce fraudulent
            claims.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What are synthetic and de-identified data, and how do they benefit insurers?"
          >
            Synthetic and de-identified data help insurers gain valuable
            insights without compromising privacy. These solutions enhance
            customer profiles and operational processes while ensuring
            compliance with data privacy regulations.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about General Genomics’ tailored solutions for the insurance industry?"
          >
            <Link to="/contact-us">Contact us</Link> today to explore how
            General Genomics’ custom data solutions can address your unique
            business needs and help optimize operations in the insurance sector.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
